// The programme's bibliography, derived from the readings attached to each
// lesson (ADR-0007) rather than maintained as a separate list that drifts.
//
// Deliberately not the same thing as the main site's Shelf, which is a
// personal record of the books that shaped the site ("Not a bibliography")
// and is curated by hand. This is the course's assigned reading, generated
// from what the lessons actually cite.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import theme from '../../theme';
import PageHeader from '../../components/PageHeader';
import { fetchBibliography } from '../../lib/lmsApi';

// Keyed on Lesson.readings[].kind (ADR-0015). Audio is deliberately distinct
// from video: "watch" a podcast is the kind of wrong wording that makes a
// course look unmaintained.
const LINK_LABEL = { text: 'read free', video: 'watch free', audio: 'listen free' };

const LEVEL_NAMES = {
  1: 'Level 1 - Foundations',
  2: 'Level 2 - Capital, Money & the Structure of Production',
  3: 'Level 3 - Business Cycles, Policy & Application',
};

const CSS = `
  .bib-intro {
    font-family: 'EB Garamond', serif;
    font-size: 17px;
    line-height: 1.75;
    color: ${theme.textPrimary};
    max-width: 65ch;
    margin: 0 0 12px;
  }
  .bib-sub {
    font-family: 'EB Garamond', serif;
    font-size: 15px;
    color: ${theme.textMuted};
    max-width: 65ch;
    margin: 0 0 32px;
    line-height: 1.7;
  }
  .bib-group { margin-bottom: 44px; }
  .bib-group h2 {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${theme.gold};
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(var(--ap-gold-rgb), 0.3);
    margin: 0 0 20px;
  }
  .bib-item {
    padding: 14px 0;
    border-bottom: 1px solid rgba(var(--ap-text-muted-rgb), 0.14);
  }
  .bib-item:last-child { border-bottom: none; }
  .bib-cite {
    font-family: 'EB Garamond', serif;
    font-size: 17px;
    color: ${theme.textPrimary};
    margin: 0 0 6px;
    line-height: 1.5;
  }
  .bib-author { font-weight: 600; }
  .bib-free {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.05em;
    margin-left: 8px;
    white-space: nowrap;
  }
  .bib-free a { color: ${theme.gold}; text-decoration: none; border-bottom: 1px solid rgba(var(--ap-gold-rgb), 0.4); }
  .bib-free a:hover { border-bottom-color: ${theme.gold}; }
  .bib-item cite { font-style: italic; }
  .bib-where {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.03em;
    color: ${theme.textMuted};
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
  }
  .bib-where a { color: ${theme.gold}; text-decoration: none; }
  .bib-where a:hover { text-decoration: underline; }
  .bib-count {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.textMuted};
    margin: 0 0 28px;
  }
  .bib-foot {
    font-family: 'EB Garamond', serif;
    font-size: 14.5px;
    color: ${theme.textMuted};
    line-height: 1.7;
    max-width: 65ch;
    margin: 8px 0 0;
    padding-top: 20px;
    border-top: 1px solid rgba(var(--ap-text-muted-rgb), 0.2);
  }
`;

// A work is filed under the earliest level that assigns it, so the list reads
// in the order a student would actually meet things.
function groupByFirstLevel(works) {
  const groups = { 1: [], 2: [], 3: [] };
  for (const w of works) {
    const first = Math.min(...w.citations.map((c) => c.level));
    (groups[first] || groups[1]).push(w);
  }
  return groups;
}

function ReadingsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Reading List - Austrian Process';
    fetchBibliography().then(setData).catch(() => setError(true));
  }, []);

  const groups = data ? groupByFirstLevel(data.works) : null;

  return (
    <div className="page-container">
      <style>{CSS}</style>
      <PageHeader
        title="Reading List"
        description="Every text the Diploma assigns, in the order you meet it."
      />

      <p className="bib-intro">
        The lessons are short by design. These are the works they send you to, and where the
        real argument lives - most of them freely available at mises.org and econlib.org.
      </p>
      <p className="bib-sub">
        Generated from the readings attached to each lesson, so it cannot drift out of step
        with the course. Chapters are cited rather than page numbers, since most of these
        exist in several editions.
      </p>

      {error && <p style={{ color: theme.error }}>Couldn't load the reading list. Please try again shortly.</p>}
      {!error && !data && <p style={{ color: theme.textMuted }}>Loading...</p>}

      {data && (
        <>
          <p className="bib-count">{data.totalWorks} works across three levels</p>
          {[1, 2, 3].map((level) => (
            groups[level].length > 0 && (
              <section key={level} className="bib-group">
                <h2>{LEVEL_NAMES[level]}</h2>
                {groups[level].map((w) => (
                  <div key={`${w.author}-${w.work}`} className="bib-item">
                    <p className="bib-cite">
                      <span className="bib-author">{w.author}</span>
                      {', '}
                      <cite>{w.work}</cite>
                      {w.url && (
                        <span className="bib-free">
                          <a href={w.url} target="_blank" rel="noopener noreferrer">
                            {LINK_LABEL[w.kind] || LINK_LABEL.text} &rarr;
                          </a>
                        </span>
                      )}
                    </p>
                    <p className="bib-where">
                      {w.citations.map((c, i) => (
                        <Link key={i} to={`/${c.courseSlug}/${c.lessonSlug}`}>
                          {c.lessonTitle}
                          {c.locator ? ` - ${c.locator}` : ''}
                        </Link>
                      ))}
                    </p>
                  </div>
                ))}
              </section>
            )
          ))}
          <p className="bib-foot">
            A work assigned in more than one lesson is listed once, under the level where you
            first meet it, with every lesson that uses it linked above.
          </p>
        </>
      )}
    </div>
  );
}

export default ReadingsPage;
